import { supabase } from '@services/supabase/supabaseClient';

export type HomeThread = {
  id: string;
  title: string;
  summary?: string | null;
  bound_ticker?: string | null;
  updated_at: string;
  created_at: string;
};

export type HomeMessage = {
  id: string;
  thread_id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
};

export async function createThread(
  title: string,
  boundTicker?: string
): Promise<{ success: boolean; id?: string; error?: string }> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { data, error } = await (supabase as any)
      .from('chat_thread')
      .insert({ user_id: user.id, title, bound_ticker: boundTicker || null })
      .select('id')
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, id: data?.id };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Failed to create thread' };
  }
}

export async function appendMessages(
  threadId: string,
  msgs: Array<{ role: 'user' | 'assistant'; content: string }>
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!msgs?.length) return { success: true };
    const payload = msgs.map(m => ({ thread_id: threadId, role: m.role, content: m.content }));
    const { error } = await (supabase as any).from('chat_message').insert(payload as any);
    if (error) return { success: false, error: error.message };

    // bump updated_at
    await (supabase as any)
      .from('chat_thread')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', threadId);
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Failed to append messages' };
  }
}

export async function listThreads(
  limit = 20
): Promise<{ success: boolean; threads?: HomeThread[]; error?: string }> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: true, threads: [] };
    const { data, error } = await (supabase as any)
      .from('chat_thread')
      .select('id, title, summary, bound_ticker, updated_at, created_at')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(limit);
    if (error) return { success: false, error: error.message };
    return { success: true, threads: (data as any) || [] };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Failed to list threads' };
  }
}

export async function getMessages(
  threadId: string,
  limit = 200
): Promise<{ success: boolean; messages?: HomeMessage[]; error?: string }> {
  try {
    const { data, error } = await (supabase as any)
      .from('chat_message')
      .select('id, thread_id, role, content, created_at')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true })
      .limit(limit);
    if (error) return { success: false, error: error.message };
    return { success: true, messages: (data as any) || [] };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Failed to get messages' };
  }
}

export async function updateSummary(
  threadId: string,
  summary: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await (supabase as any)
      .from('chat_thread')
      .update({ summary, updated_at: new Date().toISOString() })
      .eq('id', threadId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Failed to update summary' };
  }
}
